import React, { useState, useMemo } from "react";
import { T, fmt, AWARD_COLORS } from "./theme";
import { SectionHeader, Card, Input, Sel, Btn, Badge, Modal, Note, Avatar, AwardBadge } from "./ui";
import { Icon } from "./Icons";
import { calcPontosCorretor, calcAwardVitalicio } from "./awards";
import { TIME_EMOJI_SUGESTOES } from "./data";
import { supabase, isSupabaseReady } from "./supabase";

// ═══════════════════════════════════════════════════════════════════════
// TIMES ADMIN — gestão de formação de times
//
// REGRAS:
// • Mínimo: 3 corretores para formar um time válido
// • Ideal:  3 a 6 corretores
// • Máximo: 8 corretores (alerta no 9º)
// • Corretor sem time = "Corretor Pioneiro" (sem meta de time)
// ═══════════════════════════════════════════════════════════════════════

const UF_NOMES = {
  CE: "Ceará", SP: "São Paulo", PB: "Paraíba", SE: "Sergipe",
  AM: "Amazonas", RJ: "Rio de Janeiro", MG: "Minas Gerais",
  BA: "Bahia", PE: "Pernambuco", RN: "Rio Grande do Norte",
  RS: "Rio Grande do Sul", PR: "Paraná", SC: "Santa Catarina",
  GO: "Goiás", DF: "Distrito Federal", ES: "Espírito Santo",
  MT: "Mato Grosso", MS: "Mato Grosso do Sul", TO: "Tocantins",
  MA: "Maranhão", PI: "Piauí", AC: "Acre", AL: "Alagoas",
  AP: "Amapá", RO: "Rondônia", RR: "Roraima",
};

const UFS = Object.keys(UF_NOMES).sort();

const TAMANHO_MIN = 3;
const TAMANHO_MAX = 8;

export default function TimesAdmin({ times, setTimes, corretores, setCorretores }) {
  const [editando, setEditando] = useState(null);
  const [novo, setNovo] = useState(false);
  const [gerenciaCorretores, setGerenciaCorretores] = useState(null); // time cujo roster está sendo editado

  // Agrupa corretores por time (ou "sem time")
  const { porTime, semTime } = useMemo(() => {
    const map = {};
    const sem = [];
    corretores.forEach(c => {
      if (!c.timeId || !times.find(t => t.id === c.timeId && t.ativo !== false)) {
        sem.push(c);
      } else {
        if (!map[c.timeId]) map[c.timeId] = [];
        map[c.timeId].push(c);
      }
    });
    return { porTime: map, semTime: sem };
  }, [corretores, times]);

  // Stats agregados
  const timesAtivos = times.filter(t => t.ativo !== false);
  const timesValidos = timesAtivos.filter(t => (porTime[t.id]?.length || 0) >= TAMANHO_MIN);
  const timesIncompletos = timesAtivos.filter(t => (porTime[t.id]?.length || 0) < TAMANHO_MIN);
  const timesCheios = timesAtivos.filter(t => (porTime[t.id]?.length || 0) >= TAMANHO_MAX);

  async function salvarTime(t) {
    if (!isSupabaseReady) {
      // Fallback localStorage
      if (t.id) setTimes(times.map(x => x.id === t.id ? { ...x, ...t } : x));
      else {
        const novoId = t.nome.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") + "-" + Date.now().toString(36).slice(-4);
        setTimes([...times, { ...t, id: novoId, ativo: true }]);
      }
      setEditando(null); setNovo(false);
      return;
    }

    try {
      if (t.id && t._uuid) {
        // UPDATE existente
        const { error } = await supabase.from("times")
          .update({ nome: t.nome, cidade: t.cidade, uf: t.uf, emoji: t.emoji, ativo: t.ativo !== false })
          .eq("id", t._uuid);
        if (error) throw error;
        setTimes(times.map(x => x.id === t.id ? { ...x, ...t } : x));
      } else {
        // INSERT novo
        const { data, error } = await supabase.from("times").insert({
          nome: t.nome, cidade: t.cidade, uf: t.uf, emoji: t.emoji || "🏆", ativo: true,
        }).select().single();
        if (error) throw error;
        const novoId = data.nome.toLowerCase().replace(/\s+/g, "-").slice(0, 30);
        setTimes([...times, { id: novoId, _uuid: data.id, nome: data.nome, cidade: data.cidade, uf: data.uf, emoji: data.emoji, ativo: true }]);
      }
      setEditando(null); setNovo(false);
    } catch (e) {
      alert("Erro ao salvar time: " + e.message);
    }
  }

  async function desativarTime(id) {
    const time = times.find(t => t.id === id);
    const membros = porTime[id] || [];
    const msg = membros.length > 0
      ? `Desativar o time "${time.nome}"? ${membros.length} corretor(es) ficarão sem time (viram Pioneiros). Esta ação não apaga o histórico.`
      : `Desativar o time "${time.nome}"?`;
    if (!window.confirm(msg)) return;

    try {
      if (isSupabaseReady && time?._uuid) {
        await supabase.from("times").update({ ativo: false }).eq("id", time._uuid);
        // Desvincular corretores no banco
        const uuidsCorretoresAfetados = corretores.filter(c => c.timeId === id).map(c => c._uuid).filter(Boolean);
        if (uuidsCorretoresAfetados.length) {
          await supabase.from("corretores").update({ time_id: null }).in("id", uuidsCorretoresAfetados);
        }
      }
      setCorretores(corretores.map(c => c.timeId === id ? { ...c, timeId: null } : c));
      setTimes(times.map(t => t.id === id ? { ...t, ativo: false } : t));
    } catch (e) {
      alert("Erro ao desativar: " + e.message);
    }
  }

  function reativarTime(id) {
    setTimes(times.map(t => t.id === id ? { ...t, ativo: true } : t));
  }

  async function toggleMembro(timeId, corretorId) {
    const c = corretores.find(x => x.id === corretorId);
    if (!c) return;
    // Se já está nesse time, remove; senão, move pra cá
    const removendo = c.timeId === timeId;
    const novoTimeId = removendo ? null : timeId;

    // Atualiza a UI de imediato (otimista)
    setCorretores(corretores.map(x =>
      x.id === corretorId ? { ...x, timeId: novoTimeId } : x
    ));

    // Persiste no Supabase
    if (!isSupabaseReady) return;
    if (!c._uuid) {
      setCorretores(corretores.map(x => x.id === corretorId ? { ...x, timeId: c.timeId } : x));
      alert("Corretor sem UUID — recarregue a página e tente de novo.");
      return;
    }
    try {
      const time = times.find(t => t.id === timeId);
      const novoUuid = removendo ? null : (time?._uuid || null);
      if (!removendo && !novoUuid) throw new Error("Time sem UUID — recarregue a página.");
      // .select() retorna as linhas realmente afetadas → detecta 0-rows (silencioso)
      const { data, error } = await supabase
        .from("corretores")
        .update({ time_id: novoUuid })
        .eq("id", c._uuid)
        .select("id, time_id");
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error("0 linhas atualizadas (id não bateu no banco ou bloqueado). uuid=" + c._uuid);
      }
    } catch (e) {
      // Falhou: reverte a UI pro estado anterior
      setCorretores(corretores.map(x => x.id === corretorId ? { ...x, timeId: c.timeId } : x));
      alert("Erro ao salvar time do corretor: " + (e.message || e));
    }
  }

  const timesInativos = times.filter(t => t.ativo === false);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <SectionHeader
        title="Gestão de Times"
        subtitle="Crie, edite e organize os times. Mínimo 3 corretores · Máximo 8 · Corretores sem time viram Pioneiros"
        icon={<Icon.team size={24} />}
        right={<Btn onClick={() => setNovo(true)} icon={<Icon.plus size={14} />}>Criar time</Btn>}
      />

      {/* Resumo executivo */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
        <MiniKpi label="Times válidos" value={timesValidos.length} sub={`≥ ${TAMANHO_MIN} corretores`} color={T.ok} />
        <MiniKpi label="Times incompletos" value={timesIncompletos.length} sub="precisam recrutar" color={T.warn} />
        <MiniKpi label="Times cheios" value={timesCheios.length} sub={`= ${TAMANHO_MAX} · considere dividir`} color={T.brand} />
        <MiniKpi label="Corretores Pioneiros" value={semTime.length} sub="sem time" color={T.info} />
      </div>

      {/* Alertas */}
      {timesIncompletos.length > 0 && (
        <Note tone="warn" icon={<Icon.alert size={14} />}>
          <b>{timesIncompletos.length} time(s) com menos de {TAMANHO_MIN} corretores:</b> {timesIncompletos.map(t => t.nome).join(", ")}. Enquanto não atingirem o mínimo, a meta de time fica <b>pausada</b> para os membros (eles ainda participam das metas Regional e Nacional).
        </Note>
      )}

      {semTime.length > 0 && (
        <Note tone="info" icon={<Icon.user size={14} />}>
          <b>{semTime.length} corretor(es) Pioneiros</b> (sem time): {semTime.map(c => c.nome.split(" ")[0]).join(", ")}. Eles têm todos os benefícios individuais (awards, prêmios anuais, ranking) mas <b>não participam das metas semanais de time</b>. Recrute mais corretores na região deles ou adicione a um time existente.
        </Note>
      )}

      {timesCheios.length > 0 && (
        <Note tone="brand" icon={<Icon.info size={14} />}>
          <b>{timesCheios.length} time(s) no limite máximo</b> ({TAMANHO_MAX} corretores): {timesCheios.map(t => t.nome).join(", ")}. Considere criar um novo time para dividir quando recrutar o próximo corretor da região.
        </Note>
      )}

      {/* Times ativos */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 600 }}>
          Times ativos ({timesAtivos.length})
        </div>
        {timesAtivos.sort((a, b) => (porTime[b.id]?.length || 0) - (porTime[a.id]?.length || 0)).map(t => (
          <TimeCardAdmin
            key={t.id}
            time={t}
            membros={porTime[t.id] || []}
            onEditar={() => setEditando(t)}
            onDesativar={() => desativarTime(t.id)}
            onGerenciar={() => setGerenciaCorretores(t)}
          />
        ))}
        {timesAtivos.length === 0 && (
          <Card>
            <div style={{ textAlign: "center", padding: 30, color: T.textDim, fontSize: 13 }}>
              Nenhum time ativo. Clique em "Criar time" para começar.
            </div>
          </Card>
        )}
      </div>

      {/* Pioneiros (sem time) */}
      {semTime.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 600 }}>
            Corretores Pioneiros ({semTime.length}) — sem time
          </div>
          <Card soft>
            <div style={{ fontSize: 11, color: T.textDim, marginBottom: 14, lineHeight: 1.6 }}>
              🌅 Pioneiros abrem mercado em novas praças. Eles mantêm todos os benefícios individuais, mas só participam de metas coletivas a partir da Regional. Atribua-os a um time ou agrupe em um novo time quando atingir 3+ corretores na região.
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {semTime.map(c => (
                <MembroChip key={c.id} corretor={c} />
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Times desativados (histórico) */}
      {timesInativos.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 11, color: T.textDim, textTransform: "uppercase", letterSpacing: 1, fontWeight: 600 }}>
            Times desativados (histórico)
          </div>
          {timesInativos.map(t => (
            <div key={t.id} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              background: T.cardSoft, border: `1px solid ${T.border}`,
              borderRadius: T.r.md, padding: "10px 14px",
            }}>
              <div style={{ fontSize: 13, color: T.textSub }}>
                <s>{t.nome}</s> · {t.cidade}-{t.uf}
              </div>
              <Btn variant="ghost" onClick={() => reativarTime(t.id)} icon={<Icon.check size={12} />}>
                Reativar
              </Btn>
            </div>
          ))}
        </div>
      )}

      {/* Modais */}
      {(editando || novo) && (
        <TimeForm
          time={editando || { nome: "", cidade: "", uf: "CE", ativo: true }}
          onSave={salvarTime}
          onCancel={() => { setEditando(null); setNovo(false); }}
          outrosTimes={times.filter(x => x.id !== editando?.id)}
        />
      )}

      {gerenciaCorretores && (
        <GerenciaRoster
          time={gerenciaCorretores}
          corretores={corretores}
          membrosAtuais={porTime[gerenciaCorretores.id] || []}
          onToggle={corretorId => toggleMembro(gerenciaCorretores.id, corretorId)}
          onClose={() => setGerenciaCorretores(null)}
        />
      )}
    </div>
  );
}

// ─── Card de time ─────────────────────────────────────────────────────
function TimeCardAdmin({ time, membros, onEditar, onDesativar, onGerenciar }) {
  const qtd = membros.length;
  const status = qtd === 0       ? { label: "Vazio",       color: T.err,     bg: T.errSoft }
               : qtd < TAMANHO_MIN ? { label: "Incompleto", color: T.warn,    bg: T.warnSoft }
               : qtd >= TAMANHO_MAX ? { label: "Cheio",      color: T.brand,   bg: T.brandSoft }
               :                     { label: "OK",          color: T.ok,      bg: T.okSoft };

  const totalVendas = membros.reduce((s, c) => s + (c.vendasDiretas || 0), 0);
  const totalVolume = membros.reduce((s, c) => s + (c.vendasValorAno ?? c.vendasValor ?? 0), 0);

  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flex: 1, minWidth: 200 }}>
          {/* Badge emoji do time */}
          <div style={{
            width: 52, height: 52, borderRadius: T.r.md,
            background: T.cardSoft, border: `1px solid ${T.border}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 30, flexShrink: 0,
          }}>
            {time.emoji || "🏆"}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
              <div className="serif" style={{ fontSize: 18, fontWeight: 600, color: T.brand, letterSpacing: -0.2 }}>
                {time.nome}
              </div>
              <span style={{
                background: status.bg, color: status.color,
                border: `1px solid ${status.color}44`,
                borderRadius: 100, padding: "2px 10px",
                fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase",
              }}>
                {qtd}/{TAMANHO_MAX} · {status.label}
              </span>
            </div>
            <div style={{ fontSize: 11, color: T.textSub, display: "flex", gap: 10, alignItems: "center" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <Icon.pin size={10} />{time.cidade || "—"}-{time.uf || "—"}
              </span>
              {qtd > 0 && (
                <>
                  <span style={{ color: T.textDim }}>·</span>
                  <span>{totalVendas} vendas · {fmt.brl(totalVolume)}</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <Btn variant="ghost" icon={<Icon.users size={12} />} onClick={onGerenciar}>
            Gerenciar membros
          </Btn>
          <Btn variant="ghost" icon={<Icon.edit size={12} />} onClick={onEditar}>Editar</Btn>
          <Btn variant="danger" icon={<Icon.trash size={12} />} onClick={onDesativar}>Desativar</Btn>
        </div>
      </div>

      {/* Membros */}
      {qtd > 0 ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
          {membros
            .map(c => ({ ...c, pts: calcPontosCorretor(c).total, aw: calcAwardVitalicio(c) }))
            .sort((a, b) => b.pts - a.pts)
            .map(c => <MembroChip key={c.id} corretor={c} />)}
        </div>
      ) : (
        <div style={{ fontSize: 11, color: T.textDim, padding: "10px 0", borderTop: `1px solid ${T.border}`, fontStyle: "italic" }}>
          Time sem membros. Clique em "Gerenciar membros" para adicionar corretores.
        </div>
      )}
    </Card>
  );
}

// ─── Chip de corretor ─────────────────────────────────────────────────
function MembroChip({ corretor: c }) {
  const initials = c.nome.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
  const pts = calcPontosCorretor(c).total;
  const aw = calcAwardVitalicio(c);
  const color = AWARD_COLORS[aw.cor];
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      background: T.cardSoft, borderRadius: 100, padding: "4px 12px 4px 4px",
      border: `1px solid ${T.border}`,
    }}>
      <Avatar initials={initials} size={24} tone={color.fg} photo={c.foto} />
      <span style={{ fontSize: 11, color: T.text, fontWeight: 500 }}>{c.nome.split(" ")[0]}</span>
      <span style={{ fontSize: 10, color: color.fg, fontWeight: 700 }}>{pts}pts</span>
    </div>
  );
}

// ─── Form de criar/editar time ────────────────────────────────────────
function TimeForm({ time, onSave, onCancel, outrosTimes }) {
  const [t, setT] = useState({ emoji: "🏆", ...time });
  const [err, setErr] = useState("");
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [emojiCustom, setEmojiCustom] = useState(t.emoji || "");

  function handleSalvar() {
    if (!t.nome.trim()) { setErr("Nome do time é obrigatório."); return; }
    const nomeExiste = outrosTimes.some(x => x.nome.toLowerCase() === t.nome.trim().toLowerCase() && x.ativo !== false);
    if (nomeExiste) { setErr("Já existe outro time ativo com esse nome."); return; }
    onSave({ ...t, nome: t.nome.trim(), emoji: t.emoji || "🏆" });
  }

  // Import estático no topo do arquivo (TIME_EMOJI_SUGESTOES)

  return (
    <Modal title={time.id ? "Editar Time" : "Criar Time"} onClose={onCancel} width={540}>
      <Note tone="info">
        <b>Sugestão de nomenclatura:</b> [Identidade] + "de" + [Cidade]. Exemplo: "Onças-Pintadas de Fortal", "Avoantes da Paraíba". Identidades com apelo regional criam senso de pertencimento.
      </Note>

      {/* Badge visual do time */}
      <div>
        <label style={{ fontSize: 11, color: T.textSub, fontWeight: 500, letterSpacing: 0.8, textTransform: "uppercase", display: "block", marginBottom: 6 }}>
          Badge do time (emoji)
        </label>
        <div style={{ display: "flex", gap: 12, alignItems: "stretch" }}>
          <div
            onClick={() => setEmojiPickerOpen(!emojiPickerOpen)}
            style={{
              width: 72, height: 72, borderRadius: T.r.md,
              background: T.input, border: `1px solid ${T.borderMid}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 42, cursor: "pointer",
              transition: `all 160ms ${T.ease}`,
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = T.brand}
            onMouseLeave={e => e.currentTarget.style.borderColor = T.borderMid}
          >
            {t.emoji || "🏆"}
          </div>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
            <Input
              placeholder="Digite ou cole um emoji customizado"
              value={emojiCustom}
              onChange={e => {
                setEmojiCustom(e.target.value);
                if (e.target.value) setT({ ...t, emoji: e.target.value });
              }}
              hint="Toque no ícone à esquerda para abrir sugestões"
            />
          </div>
        </div>

        {emojiPickerOpen && (
          <div style={{
            marginTop: 10, padding: 12,
            background: T.cardSoft, border: `1px solid ${T.border}`,
            borderRadius: T.r.md,
            display: "grid", gridTemplateColumns: "repeat(10, 1fr)", gap: 6,
          }}>
            {TIME_EMOJI_SUGESTOES.map((e, i) => (
              <button
                key={i}
                onClick={() => {
                  setT({ ...t, emoji: e });
                  setEmojiCustom(e);
                  setEmojiPickerOpen(false);
                }}
                style={{
                  width: "100%", aspectRatio: "1",
                  background: t.emoji === e ? T.brandSoft : T.input,
                  border: `1px solid ${t.emoji === e ? T.brand : T.border}`,
                  borderRadius: T.r.sm,
                  fontSize: 22, cursor: "pointer",
                  transition: `all 120ms ${T.ease}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >{e}</button>
            ))}
          </div>
        )}
      </div>

      <Input
        label="Nome do time"
        value={t.nome}
        onChange={e => setT({ ...t, nome: e.target.value })}
        placeholder="Ex: Onças-Pintadas de Fortal"
        autoFocus
      />

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
        <Input
          label="Cidade"
          value={t.cidade}
          onChange={e => setT({ ...t, cidade: e.target.value })}
          placeholder="Ex: Fortaleza"
        />
        <Sel label="UF" value={t.uf} onChange={e => setT({ ...t, uf: e.target.value })}>
          {UFS.map(uf => <option key={uf} value={uf}>{uf} — {UF_NOMES[uf]}</option>)}
        </Sel>
      </div>

      {err && <Note tone="err" icon={<Icon.alert size={14} />}>{err}</Note>}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Btn variant="ghost" onClick={onCancel}>Cancelar</Btn>
        <Btn onClick={handleSalvar} icon={<Icon.check size={14} />}>
          {time.id ? "Salvar alterações" : "Criar time"}
        </Btn>
      </div>
    </Modal>
  );
}

// ─── Modal de gerenciar roster (adicionar/remover corretores) ─────────
function GerenciaRoster({ time, corretores, membrosAtuais, onToggle, onClose }) {
  const [busca, setBusca] = useState("");

  const membrosIds = new Set(membrosAtuais.map(m => m.id));
  const candidatos = corretores
    .filter(c => !busca || c.nome.toLowerCase().includes(busca.toLowerCase()))
    .sort((a, b) => {
      // Membros do time atual primeiro, depois outros
      const aMembro = membrosIds.has(a.id);
      const bMembro = membrosIds.has(b.id);
      if (aMembro && !bMembro) return -1;
      if (!aMembro && bMembro) return 1;
      return a.nome.localeCompare(b.nome);
    });

  const qtd = membrosAtuais.length;
  const atingiuMax = qtd >= TAMANHO_MAX;

  return (
    <Modal title={`Gerenciar membros · ${time.nome}`} onClose={onClose} width={560}>
      <Note tone={qtd < TAMANHO_MIN ? "warn" : qtd >= TAMANHO_MAX ? "brand" : "ok"}>
        <b>{qtd}/{TAMANHO_MAX} corretores no time.</b>{" "}
        {qtd < TAMANHO_MIN && `Faltam ${TAMANHO_MIN - qtd} para formar um time válido.`}
        {qtd >= TAMANHO_MIN && qtd < TAMANHO_MAX && `Time válido e com espaço (até ${TAMANHO_MAX - qtd} corretor(es) ainda).`}
        {qtd >= TAMANHO_MAX && `Time no limite máximo. Considere dividir em outro time.`}
      </Note>

      <Input
        placeholder="Buscar corretor..."
        value={busca}
        onChange={e => setBusca(e.target.value)}
      />

      <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 380, overflowY: "auto" }}>
        {candidatos.map(c => {
          const isMembro = membrosIds.has(c.id);
          const timeAtual = c.timeId && c.timeId !== time.id
            ? corretores.find(x => x.id === c.id).timeId
            : null;
          const initials = c.nome.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
          const aw = calcAwardVitalicio(c);
          const color = AWARD_COLORS[aw.cor];

          return (
            <div key={c.id} style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "10px 12px", borderRadius: T.r.md,
              background: isMembro ? T.brandSoft : T.input,
              border: `1px solid ${isMembro ? T.brand + "44" : T.border}`,
              transition: `all 160ms ${T.ease}`,
            }}>
              <Avatar initials={initials} size={32} tone={color.fg} photo={c.foto} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: T.text, fontWeight: 500 }}>{c.nome}</div>
                <div style={{ fontSize: 11, color: T.textSub }}>
                  📍 {c.cidade}-{c.uf}
                  {timeAtual && <span style={{ color: T.warn, marginLeft: 8 }}>· atualmente em outro time</span>}
                </div>
              </div>
              <AwardBadge awardId={aw.id} size="sm" />
              {isMembro ? (
                <Btn variant="danger" icon={<Icon.minus size={12} />} onClick={() => onToggle(c.id)}>
                  Remover
                </Btn>
              ) : (
                <Btn
                  variant={atingiuMax ? "ghost" : "primary"}
                  icon={<Icon.plus size={12} />}
                  onClick={() => onToggle(c.id)}
                  disabled={atingiuMax}
                >
                  {atingiuMax ? "Cheio" : "Adicionar"}
                </Btn>
              )}
            </div>
          );
        })}
        {candidatos.length === 0 && (
          <div style={{ textAlign: "center", padding: 30, color: T.textDim, fontSize: 12 }}>
            Nenhum corretor encontrado.
          </div>
        )}
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Btn onClick={onClose} icon={<Icon.check size={14} />}>Concluir</Btn>
      </div>
    </Modal>
  );
}

// ─── Mini KPI card ─────────────────────────────────────────────────────
function MiniKpi({ label, value, sub, color }) {
  return (
    <div style={{
      background: T.card, border: `1px solid ${T.border}`,
      borderRadius: T.r.md, padding: "12px 14px",
      borderLeft: `3px solid ${color}`,
    }}>
      <div style={{ fontSize: 10, color: T.textSub, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 600 }}>{label}</div>
      <div className="serif" style={{ fontSize: 24, fontWeight: 600, color: T.text, lineHeight: 1, marginTop: 4 }}>{value}</div>
      <div style={{ fontSize: 10, color, fontWeight: 500, marginTop: 3 }}>{sub}</div>
    </div>
  );
}
